import React, {useEffect, useMemo, useState} from "react";
import {
    Alert,
    Card,
    Col,
    Empty,
    Grid,
    Row,
    Select,
    Space,
    Spin,
    Statistic,
    Table,
    Tag,
    Typography,
} from "antd";
import {
    BarChartOutlined,
    CalendarOutlined,
    DollarOutlined,
    RiseOutlined,
    TrophyOutlined,
    UserOutlined,
    FundOutlined,
} from "@ant-design/icons";
import {
    Bar,
    BarChart,
    CartesianGrid,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import dayjs from "dayjs";
import api from "../../api";

const {Title, Text} = Typography;
const {useBreakpoint} = Grid;

type AgentTripRow = {
    id: number;
    mmid?: string | number | null;
    checkin_date?: string | null;
    checkout_date?: string | null;
    buyin_amount?: number | string | null;
    rebuys?: number | string | null;
    final_balance?: number | string | null;
    win_loss?: number | string | null;
    bonus_amount?: number | string | null;
    expenses?: number | string | null;
};

type AgentMonthRow = {
    month: number;
    monthly_win_loss?: number | string | null;
    opening_carryover?: number | string | null;
    adjusted_profit?: number | string | null;
    commissionable_profit?: number | string | null;
    closing_carryover?: number | string | null;
    agent_fee_rate?: number | string | null;
    agent_fees?: number | string | null;
    trips?: AgentTripRow[];
};

type AgentSettlementResponse = {
    agent: {
        id: number;
        name: string;
        program: string;
    };
    year: number;
    months: AgentMonthRow[];
};

const monthNames = [
    "",
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
];

const bigNumberStyle: React.CSSProperties = {
    fontSize: 22,
    fontWeight: 800,
    lineHeight: 1.2,
    marginTop: 2,
    whiteSpace: "nowrap",
    wordBreak: "keep-all",
    overflowWrap: "normal",
    overflow: "hidden",
    textOverflow: "ellipsis",
    fontVariantNumeric: "tabular-nums",
};

const nowrapStyle: React.CSSProperties = {
    whiteSpace: "nowrap",
    wordBreak: "keep-all",
};

const numberCellStyle: React.CSSProperties = {
    whiteSpace: "nowrap",
    wordBreak: "keep-all",
    fontVariantNumeric: "tabular-nums",
};

const mobileFieldLabelStyle: React.CSSProperties = {
    fontSize: 12,
    color: "#6b7280",
};

const mobileFieldValueStyle: React.CSSProperties = {
    ...numberCellStyle,
    fontWeight: 700,
    marginTop: 2,
};

const MobileField = ({
                         label,
                         value,
                     }: {
    label: string;
    value: React.ReactNode;
}) => (
    <div>
        <div style={mobileFieldLabelStyle}>{label}</div>
        <div style={mobileFieldValueStyle}>{value}</div>
    </div>
);

const renderHeader = (label: string) => (
    <span style={nowrapStyle}>{label}</span>
);

const renderKRWCell = (value: number | string | null | undefined) => (
    <span style={numberCellStyle}>{formatKRW(value)}</span>
);

const renderPercentCell = (value: number | string | null | undefined) => (
    <span style={numberCellStyle}>{formatPercent(value)}</span>
);

const toNumber = (value: number | string | null | undefined) => {
    if (value === null || value === undefined || value === "") return 0;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

const formatKRW = (value: number | string | null | undefined) => {
    const num = toNumber(value);
    return `₩${Math.round(num).toLocaleString()}`;
};

const formatCompactKRW = (value: number | string | null | undefined) => {
    const num = toNumber(value);

    if (Math.abs(num) >= 1_000_000_000) {
        return `₩${(num / 1_000_000_000).toFixed(1)}B`;
    }

    if (Math.abs(num) >= 1_000_000) {
        return `₩${(num / 1_000_000).toFixed(1)}M`;
    }

    return `₩${Math.round(num).toLocaleString()}`;
};

const formatPercent = (value: number | string | null | undefined) => {
    const num = toNumber(value);
    return `${(num * 100).toFixed(1)}%`;
};

const AgentAnalyticsPage = () => {
    const screens = useBreakpoint();
    const isMobile = !screens.md;
    const [year, setYear] = useState(dayjs().year());
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [data, setData] = useState<AgentSettlementResponse | null>(null);

    useEffect(() => {
        const fetchAnalytics = async () => {
            setLoading(true);
            setError(null);

            try {
                const response = await api.get<AgentSettlementResponse>(
                    `/daia/agent-portal/settlements/?year=${year}`
                );

                setData(response.data);
            } catch (err: any) {
                const msg =
                    err.response?.data?.detail ||
                    err.response?.data?.error ||
                    "Failed to load analytics data.";

                setError(msg);
            } finally {
                setLoading(false);
            }
        };

        fetchAnalytics();
    }, [year]);

    const months = data?.months || [];

    const analytics = useMemo(() => {
        const ytdWinLoss = months.reduce(
            (sum, row) => sum + toNumber(row.monthly_win_loss),
            0
        );

        const ytdCommissionableProfit = months.reduce(
            (sum, row) => sum + toNumber(row.commissionable_profit),
            0
        );

        const ytdAgentFees = months.reduce(
            (sum, row) => sum + toNumber(row.agent_fees),
            0
        );

        const totalTrips = months.reduce(
            (sum, row) => sum + (row.trips?.length || 0),
            0
        );

        const activeMonths = months.filter((row) => {
            return (
                toNumber(row.monthly_win_loss) !== 0 ||
                toNumber(row.adjusted_profit) !== 0 ||
                toNumber(row.agent_fees) !== 0 ||
                (row.trips?.length || 0) > 0
            );
        });

        const bestMonth = [...activeMonths].sort(
            (a, b) => toNumber(b.agent_fees) - toNumber(a.agent_fees)
        )[0];

        const customerTotals = new Map<string, number>();

        months.forEach((month) => {
            month.trips?.forEach((trip) => {
                if (!trip.mmid) return;

                const mmid = String(trip.mmid);
                const winLoss = Math.max(toNumber(trip.win_loss), 0);

                customerTotals.set(mmid, (customerTotals.get(mmid) || 0) + winLoss);
            });
        });

        const bestCustomerEntry = Array.from(customerTotals.entries()).sort(
            (a, b) => b[1] - a[1]
        )[0];

        const bestCustomer = bestCustomerEntry
            ? {
                mmid: bestCustomerEntry[0],
                winLoss: bestCustomerEntry[1],
            }
            : null;

        const latestMonthWithData = [...activeMonths].sort(
            (a, b) => b.month - a.month
        )[0];

        const currentCarryover = latestMonthWithData
            ? toNumber(latestMonthWithData.closing_carryover)
            : months.length
                ? toNumber(months[months.length - 1].closing_carryover)
                : 0;

        const chartData = months.map((row) => ({
            month: monthNames[row.month] || String(row.month),
            winLoss: toNumber(row.monthly_win_loss),
            adjustedProfit: toNumber(row.adjusted_profit),
            commissionableProfit: toNumber(row.commissionable_profit),
            agentFees: toNumber(row.agent_fees),
            carryover: toNumber(row.closing_carryover),
            trips: row.trips?.length || 0,
        }));

        return {
            ytdWinLoss,
            ytdCommissionableProfit,
            ytdAgentFees,
            totalTrips,
            bestMonth,
            bestCustomer,
            currentCarryover,
            chartData,
        };
    }, [months]);

    const monthlyColumns = [
        {
            title: renderHeader("Month"),
            dataIndex: "month",
            key: "month",
            width: 80,
            render: (month: number) => (
                <span style={nowrapStyle}>{monthNames[month] || month}</span>
            ),
        },
        {
            title: renderHeader("Win/Loss"),
            dataIndex: "monthly_win_loss",
            key: "monthly_win_loss",
            align: "right" as const,
            width: 150,
            ellipsis: false,
            render: renderKRWCell,
        },
        {
            title: renderHeader("Adjusted Monthly Profit"),
            dataIndex: "adjusted_profit",
            key: "adjusted_profit",
            align: "right" as const,
            width: 190,
            ellipsis: false,
            render: renderKRWCell,
        },
        {
            title: renderHeader("Commissionable Profit"),
            dataIndex: "commissionable_profit",
            key: "commissionable_profit",
            align: "right" as const,
            width: 190,
            ellipsis: false,
            render: renderKRWCell,
        },
        {
            title: renderHeader("Fee Rate"),
            dataIndex: "agent_fee_rate",
            key: "agent_fee_rate",
            align: "right" as const,
            width: 110,
            render: renderPercentCell,
        },
        {
            title: renderHeader("Agent Fees"),
            dataIndex: "agent_fees",
            key: "agent_fees",
            align: "right" as const,
            width: 150,
            ellipsis: false,
            render: renderKRWCell,
        },
        {
            title: renderHeader("Carryover"),
            dataIndex: "closing_carryover",
            key: "closing_carryover",
            align: "right" as const,
            width: 150,
            ellipsis: false,
            render: renderKRWCell,
        },
        {
            title: renderHeader("Trips"),
            key: "trips",
            align: "right" as const,
            width: 80,
            render: (_: any, row: AgentMonthRow) => (
                <span style={numberCellStyle}>{row.trips?.length || 0}</span>
            ),
        },
    ];

    const renderMobileMonthCard = (row: AgentMonthRow) => {
        const tripCount = row.trips?.length || 0;
        const agentFees = toNumber(row.agent_fees);
        const winLoss = toNumber(row.monthly_win_loss);

        return (
            <Card
                key={row.month}
                style={{
                    borderRadius: 16,
                    border: "1px solid #e5e7eb",
                }}
                bodyStyle={{padding: 16}}
            >
                <Space direction="vertical" size={14} style={{width: "100%"}}>
                    <Row justify="space-between" align="middle" gutter={[8, 8]}>
                        <Col>
                            <Text type="secondary">Month</Text>
                            <div
                                style={{
                                    fontSize: 21,
                                    fontWeight: 800,
                                    lineHeight: 1.2,
                                }}
                            >
                                {monthNames[row.month] || row.month}
                            </div>
                        </Col>

                        <Col>
                            <Tag color={tripCount ? "blue" : "default"}>
                                {tripCount} Trip{tripCount === 1 ? "" : "s"}
                            </Tag>
                        </Col>
                    </Row>

                    <Row gutter={[12, 12]}>
                        <Col span={12}>
                            <MobileField
                                label="Win/Loss"
                                value={
                                    <span
                                        style={{
                                            color: winLoss < 0 ? "#cf1322" : "#1677ff",
                                        }}
                                    >
                                    {formatKRW(row.monthly_win_loss)}
                                </span>
                                }
                            />
                        </Col>

                        <Col span={12}>
                            <MobileField
                                label="Agent Fees"
                                value={formatKRW(row.agent_fees)}
                            />
                        </Col>

                        <Col span={12}>
                            <MobileField
                                label="Commissionable Profit"
                                value={formatKRW(row.commissionable_profit)}
                            />
                        </Col>

                        <Col span={12}>
                            <MobileField
                                label="Fee Rate"
                                value={formatPercent(row.agent_fee_rate)}
                            />
                        </Col>

                        <Col span={12}>
                            <MobileField
                                label="Adjusted Monthly Profit"
                                value={formatKRW(row.adjusted_profit)}
                            />
                        </Col>

                        <Col span={12}>
                            <MobileField
                                label="Carryover"
                                value={formatKRW(row.closing_carryover)}
                            />
                        </Col>
                    </Row>

                    {agentFees > 0 && (
                        <div
                            style={{
                                padding: "10px 12px",
                                borderRadius: 12,
                                background: "#f6ffed",
                                border: "1px solid #b7eb8f",
                            }}
                        >
                            <Text type="secondary" style={{fontSize: 12}}>
                                Commission earned
                            </Text>
                            <div
                                style={{
                                    ...bigNumberStyle,
                                    fontSize: 20,
                                    color: "#389e0d",
                                }}
                            >
                                {formatKRW(agentFees)}
                            </div>
                        </div>
                    )}
                </Space>
            </Card>
        );
    };

    const visibleMobileMonths = months.filter((row) => {
        return (
            toNumber(row.monthly_win_loss) !== 0 ||
            toNumber(row.commissionable_profit) !== 0 ||
            toNumber(row.agent_fees) !== 0 ||
            toNumber(row.closing_carryover) !== 0 ||
            (row.trips?.length || 0) > 0
        );
    });

    return (
        <div>
            <div
                style={{
                    marginBottom: 16,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: 12,
                    flexWrap: "wrap",
                }}
            >
                <div>
                    <Title level={3} style={{marginBottom: 4}}>
                        Analytics
                    </Title>
                    <Text type="secondary">
                        {data?.agent?.name
                            ? `${data.agent.name} · ${data.agent.program} Program`
                            : "Review your year-to-date settlement performance and monthly trends."}
                    </Text>
                </div>

                <Select
                    value={year}
                    onChange={setYear}
                    style={{width: 120}}
                    options={[
                        {value: dayjs().year(), label: String(dayjs().year())},
                        {value: dayjs().year() - 1, label: String(dayjs().year() - 1)},
                        {value: dayjs().year() - 2, label: String(dayjs().year() - 2)},
                        {value: dayjs().year() - 3, label: String(dayjs().year() - 3)},
                    ]}
                />
            </div>

            {error && (
                <Alert
                    type="error"
                    message="Unable to load analytics"
                    description={error}
                    showIcon
                    style={{marginBottom: 16}}
                />
            )}

            {loading ? (
                <Card>
                    <div
                        style={{
                            minHeight: 240,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <Spin size="large"/>
                    </div>
                </Card>
            ) : (
                <>
                    <Row gutter={[16, 16]} style={{marginBottom: 16}}>
                        <Col xs={24} sm={12} lg={6}>
                            <Card>
                                <Statistic
                                    title="YTD Win/Loss"
                                    value={analytics.ytdWinLoss}
                                    formatter={formatKRW}
                                    prefix={<DollarOutlined/>}
                                    valueStyle={{
                                        fontSize: 22,
                                        color: analytics.ytdWinLoss >= 0 ? "#1677ff" : "#cf1322",
                                    }}
                                />
                            </Card>
                        </Col>

                        <Col xs={24} sm={12} lg={6}>
                            <Card>
                                <Statistic
                                    title="YTD Commissions"
                                    value={analytics.ytdAgentFees}
                                    formatter={formatKRW}
                                    prefix={<RiseOutlined/>}
                                    valueStyle={{fontSize: 22}}
                                />
                            </Card>
                        </Col>

                        <Col xs={24} sm={12} lg={6}>
                            <Card>
                                <Statistic
                                    title="Trips"
                                    value={analytics.totalTrips}
                                    prefix={<CalendarOutlined/>}
                                    valueStyle={{fontSize: 22}}
                                />
                            </Card>
                        </Col>

                        <Col xs={24} sm={12} lg={6}>
                            <Card>
                                <Statistic
                                    title="Current Carryover"
                                    value={analytics.currentCarryover}
                                    formatter={formatKRW}
                                    prefix={<BarChartOutlined/>}
                                    valueStyle={{
                                        fontSize: 22,
                                        color: analytics.currentCarryover < 0 ? "#cf1322" : undefined,
                                    }}
                                />
                            </Card>
                        </Col>
                    </Row>

                    <Row gutter={[16, 16]} style={{marginBottom: 16}}>
                        <Col xs={24} lg={16}>
                            <Card title="Monthly Commissions" bodyStyle={{height: 340}}>
                                {analytics.chartData.some((row) => row.agentFees !== 0) ? (
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={analytics.chartData}>
                                            <CartesianGrid strokeDasharray="3 3"/>
                                            <XAxis dataKey="month"/>
                                            <YAxis tickFormatter={formatCompactKRW}/>
                                            <Tooltip formatter={(value: any) => formatKRW(value)}/>
                                            <Bar dataKey="agentFees" name="Commissions"/>
                                        </BarChart>
                                    </ResponsiveContainer>
                                ) : (
                                    <Empty description="No agent fee data for this year"/>
                                )}
                            </Card>
                        </Col>

                        <Col xs={24} lg={8}>
                            <Card title="Highlights" style={{height: "100%"}}>
                                <div style={{display: "grid", gap: 16}}>
                                    <Card
                                        size="small"
                                        style={{
                                            background: "linear-gradient(135deg, #fff7e6 0%, #ffffff 100%)",
                                            border: "1px solid #ffd591",
                                        }}
                                        bodyStyle={{padding: 16}}
                                    >
                                        <div style={{display: "flex", alignItems: "center", gap: 12}}>
                                            <div
                                                style={{
                                                    width: 42,
                                                    height: 42,
                                                    borderRadius: 12,
                                                    background: "#faad14",
                                                    color: "#fff",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    fontSize: 20,
                                                    flexShrink: 0,
                                                }}
                                            >
                                                <TrophyOutlined/>
                                            </div>

                                            <div style={{minWidth: 0}}>
                                                <Text type="secondary">Best Month by Commissions</Text>
                                                <div
                                                    style={{
                                                        fontSize: 22,
                                                        fontWeight: 800,
                                                        lineHeight: 1.2,
                                                        marginTop: 2,
                                                    }}
                                                >
                                                    {analytics.bestMonth
                                                        ? monthNames[analytics.bestMonth.month] ||
                                                        analytics.bestMonth.month
                                                        : "—"}
                                                </div>
                                                <Text strong>
                                                    {analytics.bestMonth
                                                        ? formatKRW(analytics.bestMonth.agent_fees)
                                                        : "No fees yet"}
                                                </Text>
                                            </div>
                                        </div>
                                    </Card>

                                    <Card
                                        size="small"
                                        style={{
                                            background: "linear-gradient(135deg, #e6f4ff 0%, #ffffff 100%)",
                                            border: "1px solid #91caff",
                                        }}
                                        bodyStyle={{padding: 16}}
                                    >
                                        <div style={{display: "flex", alignItems: "center", gap: 12}}>
                                            <div
                                                style={{
                                                    width: 42,
                                                    height: 42,
                                                    borderRadius: 12,
                                                    background: "#1677ff",
                                                    color: "#fff",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    fontSize: 20,
                                                    flexShrink: 0,
                                                }}
                                            >
                                                <UserOutlined/>
                                            </div>

                                            <div style={{minWidth: 0}}>
                                                <Text type="secondary">Best Customer</Text>
                                                <div
                                                    style={{
                                                        fontSize: 22,
                                                        fontWeight: 800,
                                                        lineHeight: 1.2,
                                                        marginTop: 2,
                                                    }}
                                                >
                                                    {analytics.bestCustomer
                                                        ? analytics.bestCustomer.mmid
                                                        : "—"}
                                                </div>
                                                <Text strong>
                                                    {analytics.bestCustomer
                                                        ? formatKRW(analytics.bestCustomer.winLoss)
                                                        : "No customer data yet"}
                                                </Text>
                                            </div>
                                        </div>
                                    </Card>

                                    <Card
                                        size="small"
                                        style={{
                                            background: "linear-gradient(135deg, #f6ffed 0%, #ffffff 100%)",
                                            border: "1px solid #b7eb8f",
                                        }}
                                        bodyStyle={{padding: 16}}
                                    >
                                        <div style={{display: "flex", alignItems: "center", gap: 12}}>
                                            <div
                                                style={{
                                                    width: 42,
                                                    height: 42,
                                                    borderRadius: 12,
                                                    background: "#52c41a",
                                                    color: "#fff",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    fontSize: 20,
                                                    flexShrink: 0,
                                                }}
                                            >
                                                <FundOutlined/>
                                            </div>

                                            <div style={{minWidth: 0}}>
                                                <Text type="secondary">YTD Commissionable Profit</Text>
                                                <div
                                                    style={{
                                                        fontSize: 22,
                                                        fontWeight: 800,
                                                        lineHeight: 1.2,
                                                        marginTop: 2,
                                                    }}
                                                >
                                                    {formatKRW(analytics.ytdCommissionableProfit)}
                                                </div>
                                                <Text strong>
                                                    Used to calculate commissions
                                                </Text>
                                            </div>
                                        </div>
                                    </Card>
                                </div>
                            </Card>
                        </Col>
                    </Row>

                    <Card
                        title="Monthly Breakdown"
                        style={{
                            borderRadius: 16,
                            border: "1px solid #e5e7eb",
                        }}
                        bodyStyle={{padding: isMobile ? 12 : 16}}
                    >
                        {months.length ? (
                            isMobile ? (
                                <Space direction="vertical" size={14} style={{width: "100%"}}>
                                    {visibleMobileMonths.map(renderMobileMonthCard)}
                                </Space>
                            ) : (
                                <Table
                                    className="agent-analytics-table"
                                    rowKey={(row) => String(row.month)}
                                    columns={monthlyColumns}
                                    dataSource={months}
                                    pagination={false}
                                    scroll={{x: "max-content"}}
                                    size="middle"
                                />
                            )
                        ) : (
                            <Empty description="No settlement data for this year"/>
                        )}
                    </Card>
                </>
            )}
        </div>
    );
};

export default AgentAnalyticsPage;